<template>
  <BlockFieldset :class="`project-technical-review-${side}`">
    <b-row>
      <b-col lg="6" md="6" sm="12">
        <b-form-group
          horizontal
          :label="$t(sideConfig.stateField)"
          :label-for="sideConfig.stateField"
          label-class="text-md-right"
          :label-cols="4"
          breakpoint="md"
        >
          <multiselect
            :id="sideConfig.stateField"
            v-model="selectedInspectedState"
            :placeholder="$t(sideConfig.stateField)"
            :options="inspectedStateOptions"
            track-by="value"
            label="value"
            :show-labels="false"
            :allow-empty="false"
            :disabled="isBlockReadOnly"
          >
            <template slot="singleLabel" slot-scope="{ option }">
              <b-badge :variant="getYesNoStateColor(option.value)">{{ $t(option.value) }}</b-badge>
            </template>
            <template slot="option" slot-scope="{ option }">
              <b-badge :variant="getYesNoStateColor(option.value)">{{ $t(option.value) }}</b-badge>
            </template>
          </multiselect>
          <small v-if="isIncompleteHintShown" class="text-danger">
            {{ $t('technicalReviewIncomplete') }}
          </small>
        </b-form-group>
      </b-col>

      <b-col lg="6" md="6" sm="12">
        <b-form-group
          horizontal
          :label="$t(sideConfig.durationField)"
          :label-for="sideConfig.durationField"
          label-class="text-md-right"
          :label-cols="4"
          breakpoint="md"
          :data-field="sideConfig.durationField"
          :class="requiredFieldClass(sideConfig.durationField)"
        >
          <template v-if="isDurationHiddenForGuest">
            <span v-if="isDurationMissing" class="text-danger">
              {{ $t('plannedDurationMissing') }}
            </span>
          </template>
          <UnitInputNumber
            v-else
            :id="sideConfig.durationField"
            v-model="durationInHours"
            :disabled="isBlockReadOnly"
            unit="unit_hours"
          />
        </b-form-group>
      </b-col>
    </b-row>

    <b-row>
      <b-col lg="12">
        <b-form-group
          v-for="fieldName in sideConfig.richTextFields"
          :key="fieldName"
          horizontal
          :label="$t(fieldName)"
          :label-for="fieldName"
          label-class="text-md-right"
          :label-cols="2"
          breakpoint="md"
          :data-field="fieldName"
          :class="requiredFieldClass(fieldName)"
        >
          <div :id="fieldName" class="dense-editor">
            <ckeditor
              :editor="editor"
              v-model="project.technicalReview[fieldName]"
              :config="editorConfig"
              :disabled="isBlockReadOnly"
            ></ckeditor>
          </div>
        </b-form-group>
      </b-col>
    </b-row>

    <div v-if="hasSaveButton" class="text-right mt-2">
      <b-button variant="bg" :disabled="isSaving" @click="save">
        {{ $t('saveNamed', { name: $t(sideConfig.labelKey) }) }}
      </b-button>
    </div>
  </BlockFieldset>
</template>

<script>
import { mapActions, mapGetters } from 'vuex';
import Multiselect from 'vue-multiselect';
import CKEditor from '@ckeditor/ckeditor5-vue2';
import InlineEditor from '@ckeditor/ckeditor5-build-inline';
import BlockFieldset from '@/components/Access/BlockFieldset.vue';
import UnitInputNumber from '@/components/UnitInputNumber.vue';
import { AccessBlockMixin } from '@/mixins/accessBlock';
import { BlockSaveMixin } from '@/mixins/blockSave';
import { yesNo } from '@/helpers/enums';
import { getYesNoStateColor } from '@/helpers/colors';
import { hoursToSeconds, secondsToHours } from '@/helpers/duration';
import { findMissingInspectionFields } from './technicalReviewCompleteness';
import { TECHNICAL_REVIEW_SIDES } from './technicalReviewSides';

/** Number of the unsaved project (ProjectEdit `_new`): its values go with `createProject`. */
const NEW_PROJECT_NUMBER = '_new';
const INSPECTED = 'YES';

/**
 * One side of the technical review as its block (see feature page projekt-technische-pruefung):
 * project-technical-review-dc ("Techn. Prüfung - DC") or project-technical-review-ac ("Techn.
 * Prüfung - AC"); the fields come from `TECHNICAL_REVIEW_SIDES`. First row state and planned
 * duration, below the rich text fields full width; own save button. "Ja" can be selected only
 * while the required fields are filled; only this block checks it, the API saves without the
 * rule. In Legacy-Authz guests do not see the planned duration, only the hint when it is missing.
 * On the unsaved project the fields go with `createProject`. Emits `dirty` and `saved`.
 */
export default {
  name: 'ProjectTechnicalReviewSide',
  components: { BlockFieldset, Multiselect, UnitInputNumber, ckeditor: CKEditor.component },
  mixins: [AccessBlockMixin, BlockSaveMixin],
  props: {
    blockId: { type: String, required: true },
    project: { type: Object, required: true },
    /** Key of `TECHNICAL_REVIEW_SIDES`. */
    side: {
      type: String,
      required: true,
      validator: (value) => Object.keys(TECHNICAL_REVIEW_SIDES).includes(value),
    },
  },
  data() {
    return {
      editor: InlineEditor,
      editorConfig: {},
    };
  },
  computed: {
    ...mapGetters('auth', ['isOrganizationRoleNavigationEnabled', 'isGuest']),
    sideConfig() {
      return TECHNICAL_REVIEW_SIDES[this.side];
    },
    blockInput() {
      return this.sideConfig.toInput(this.project);
    },
    hasSaveButton() {
      return !this.isBlockReadOnly && this.project.number !== NEW_PROJECT_NUMBER;
    },
    missingFields() {
      return findMissingInspectionFields(
        this.project.technicalReview,
        this.sideConfig.requiredFields
      );
    },
    isComplete() {
      return this.missingFields.length === 0;
    },
    isIncompleteHintShown() {
      return !this.isBlockReadOnly && !this.isComplete;
    },
    /** Options as objects, so vue-multiselect can disable "Ja" while required fields are empty. */
    inspectedStateOptions() {
      return yesNo().map((value) => ({
        value,
        $isDisabled: value === INSPECTED && !this.isComplete,
      }));
    },
    selectedInspectedState: {
      get() {
        return (
          this.inspectedStateOptions.find(
            (option) => option.value === this.project.technicalReview[this.sideConfig.stateField]
          ) ?? null
        );
      },
      set(option) {
        this.$set(this.project.technicalReview, this.sideConfig.stateField, option.value);
      },
    },
    durationInHours: {
      get() {
        return secondsToHours(this.project.technicalReview[this.sideConfig.durationField]);
      },
      set(hours) {
        this.$set(
          this.project.technicalReview,
          this.sideConfig.durationField,
          hoursToSeconds(hours)
        );
      },
    },
    /** Legacy-Authz rule: guests do not see the planned duration; in Matrix-Authz the level decides. */
    isDurationHiddenForGuest() {
      return !this.isOrganizationRoleNavigationEnabled && this.isGuest;
    },
    isDurationMissing() {
      return this.missingFields.includes(this.sideConfig.durationField);
    },
  },
  methods: {
    ...mapActions(['updateProjectTechnicalReviewDc', 'updateProjectTechnicalReviewAc']),
    getYesNoStateColor,
    saveBlockInput(input) {
      return this[this.sideConfig.saveAction]({ projectNumber: this.project.number, input });
    },
    requiredFieldClass(fieldName) {
      return { 'is-missing': this.isIncompleteHintShown && this.missingFields.includes(fieldName) };
    },
  },
};
</script>

<style scoped lang="scss">
.dense-editor {
  height: auto;
  max-height: 300px;
  overflow-y: auto;
}

.ck-editor__editable_inline {
  min-height: 110px;
  height: auto;
  max-height: 300px;
  overflow-y: auto;
}

.is-missing .dense-editor,
.is-missing ::v-deep .p-inputnumber input {
  border: 1px solid var(--danger, #dc3545);
  border-radius: 0.25rem;
}
</style>
